/**
 * ControlPanel API (`/free-text-templates`) クライアント。
 *
 * 任意テキストテンプレートマスタ (FreeTextTemplateMaster) を CRUD する。
 * 放送地点マスタの任意テキスト放送「放送文面 (通常テキスト)」に流し込む定型の文面を
 * 線区別に管理する。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 * - 個別アイテムは `(route_id, template_id)` の複合キーなので 2 階層のパスになる
 *   (`free-text-templates/{route_id}/{template_id}`)。`route_id` は日本語を含むため
 *   **必ず URL エンコード**して送る。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './common';
import type {
  ApiErrorBody,
  FreeTextTemplateCreateRequest,
  FreeTextTemplateDeleteResponse,
  FreeTextTemplateListResponse,
  FreeTextTemplateSaveResult,
  FreeTextTemplateUpdateRequest,
} from './types';

export interface FreeTextTemplatesClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class FreeTextTemplatesClient {
  private readonly options: FreeTextTemplatesClientOptions;

  constructor(options: FreeTextTemplatesClientOptions) {
    this.options = options;
  }

  /**
   * GET /free-text-templates (テンプレート一覧)
   *
   * 並びは全線区共通 → 各線区 → 本文。
   *
   * @param routeId 指定すると「その線区 + 全線区共通」のみに絞り込む (省略時は全件)
   */
  async list(routeId?: string): Promise<FreeTextTemplateListResponse> {
    const query = routeId ? `?route_id=${encodeURIComponent(routeId)}` : '';
    return this.request<FreeTextTemplateListResponse>('GET', `free-text-templates${query}`);
  }

  /**
   * POST /free-text-templates (テンプレートを登録)
   *
   * `template_id` はサーバが採番する。同じ線区に同じ本文があっても保存され、
   * レスポンスの `warnings` に `duplicate_text` が入る。
   */
  async create(params: {
    routeId: string;
    title: string;
    text: string;
    note?: string;
  }): Promise<FreeTextTemplateSaveResult> {
    const payload: FreeTextTemplateCreateRequest = {
      route_id: params.routeId,
      title: params.title,
      text: params.text,
      ...(params.note !== undefined && { note: params.note }),
    };
    return this.request<FreeTextTemplateSaveResult>('POST', 'free-text-templates', payload);
  }

  /**
   * PUT /free-text-templates/{route_id}/{template_id} (本文を更新)
   *
   * `route_id` はキーなので変更できない (変えたいときは削除して登録し直す)。
   */
  async update(
    routeId: string,
    templateId: string,
    params: { text: string; title?: string; note?: string },
  ): Promise<FreeTextTemplateSaveResult> {
    const payload: FreeTextTemplateUpdateRequest = {
      text: params.text,
      ...(params.title !== undefined && { title: params.title }),
      ...(params.note !== undefined && { note: params.note }),
    };
    return this.request<FreeTextTemplateSaveResult>(
      'PUT',
      this.itemPath(routeId, templateId),
      payload,
    );
  }

  /** DELETE /free-text-templates/{route_id}/{template_id} (テンプレートを削除) */
  async remove(routeId: string, templateId: string): Promise<FreeTextTemplateDeleteResponse> {
    return this.request<FreeTextTemplateDeleteResponse>(
      'DELETE',
      this.itemPath(routeId, templateId),
    );
  }

  // ---------------------------------------------------------------------
  // 内部実装
  // ---------------------------------------------------------------------

  /** 個別アイテムのパス (2 階層のパスパラメータ) を組み立てる。 */
  private itemPath(routeId: string, templateId: string): string {
    return `free-text-templates/${encodeURIComponent(routeId)}/${encodeURIComponent(templateId)}`;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? `API request failed: HTTP ${res.status}`;
      throw new ApiError(res.status, message, errorBody);
    }

    return (await res.json()) as T;
  }
}

export { ApiError };
